import type {ReactNode} from 'react';

const WIDTH = 340;
const HEIGHT = 214;

function wavePath(baseline: number, amplitude: number, frequency: number, phase: number): string {
    const points: string[] = [];
    for (let x = -10; x <= WIDTH + 10; x += 6) {
        const y = baseline + amplitude * Math.sin(x * frequency + phase);
        points.push(`${x === -10 ? 'M' : 'L'}${x} ${y.toFixed(1)}`);
    }
    return points.join(' ');
}

const GUILLOCHE_PATHS = Array.from({length: 26}, (_, index) =>
    wavePath(-20 + index * 10, 16 + (index % 3) * 4, 0.028, index * 0.42),
);

const CONTOUR_PATHS = Array.from({length: 9}, (_, index) =>
    wavePath(40 + index * 22, 18 - index, 0.018 + index * 0.0012, index * 0.9),
);

const RING_RADII = Array.from({length: 16}, (_, index) => 30 + index * 22);

function OrnamentSvg({children}: {children: ReactNode}) {
    return (
        <svg
            aria-hidden
            className="absolute inset-0 h-full w-full"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="xMidYMid slice"
            fill="none"
        >
            {children}
        </svg>
    );
}

export function Guilloche({color, opacity}: {color: string; opacity: number}) {
    return (
        <OrnamentSvg>
            <g stroke={color} strokeOpacity={opacity} strokeWidth="0.6">
                {GUILLOCHE_PATHS.map((path) => <path key={path} d={path} />)}
            </g>
        </OrnamentSvg>
    );
}

export function Rings({color, opacity}: {color: string; opacity: number}) {
    return (
        <OrnamentSvg>
            <g stroke={color} strokeOpacity={opacity} strokeWidth="0.8">
                {RING_RADII.map((radius) => <circle key={radius} cx={WIDTH - 20} cy={-10} r={radius} />)}
            </g>
        </OrnamentSvg>
    );
}

export function Orbs({color}: {color: string}) {
    return (
        <OrnamentSvg>
            <circle cx={WIDTH - 40} cy={10} r={120} fill={color} fillOpacity="0.09" />
            <circle cx={WIDTH - 130} cy={HEIGHT + 30} r={110} fill={color} fillOpacity="0.07" />
            <circle cx={60} cy={HEIGHT - 10} r={70} fill={color} fillOpacity="0.05" />
        </OrnamentSvg>
    );
}

export function BrushedMetal() {
    return (
        <>
            <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.025)_0_1px,transparent_1px_3px)]" />
            <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent_30%,rgba(255,255,255,0.08)_45%,transparent_60%)]" />
        </>
    );
}

export function Contours({color, opacity}: {color: string; opacity: number}) {
    return (
        <OrnamentSvg>
            <g stroke={color} strokeOpacity={opacity} strokeWidth="0.9">
                {CONTOUR_PATHS.map((path) => <path key={path} d={path} />)}
            </g>
        </OrnamentSvg>
    );
}
